/**
 * Shared invoice/receipt builder used by PaymentsPage and InvoiceHistoryPage.
 *
 * Produces an A4-ready, insurance-grade HTML invoice that can be reprinted
 * from the receipt dialog or from history.
 */

const METHOD_LABEL = {
  cash: 'Cash', card: 'Card', upi: 'UPI', bank_transfer: 'Bank Transfer',
  netbanking: 'Net Banking', insurance: 'Insurance', cheque: 'Cheque', wallet: 'Wallet',
};

const calcAge = (dob) => {
  if (!dob) return '';
  const d = new Date(dob);
  if (isNaN(d)) return '';
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age >= 0 ? `${age} yrs` : '';
};

const formatMoney = (n) =>
  `Rs. ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDateTime = (iso) => {
  const d = new Date(iso || Date.now());
  if (isNaN(d)) return '';
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

export function buildReceiptHTML({ receipt, patient, hospital = {} }) {
  const p = patient || receipt.patient || {};
  const splits = Array.isArray(receipt.splits) && receipt.splits.length
    ? receipt.splits
    : [{ method: receipt.payment_method || 'cash', amount: receipt.amount, reference: '' }];
  const baseAmount = receipt.base_amount ?? receipt.amount ?? 0;
  const taxAmount = receipt.tax_amount ?? 0;
  const grand = receipt.amount ?? (baseAmount + taxAmount);
  const issued = formatDateTime(receipt.created_at);
  const fullName = `${p.title || ''} ${p.first_name || ''} ${p.last_name || ''}`.trim();
  const primaryColor = hospital.primary_color || '#0d9488';
  const accentColor = hospital.accent_color || '#10b981';

  const splitsRows = splits.map((s) => `
    <tr>
      <td style="padding:6px 10px">${METHOD_LABEL[s.method] || s.method}</td>
      <td style="padding:6px 10px;color:#64748b">${s.reference || '—'}</td>
      <td style="padding:6px 10px;text-align:right;font-variant-numeric:tabular-nums">${formatMoney(s.amount)}</td>
    </tr>`).join('');

  const logoHtml = hospital.logo_url
    ? `<img src="${hospital.logo_url}" alt="logo" style="height:48px;width:48px;border-radius:8px;background:#fff;padding:4px;object-fit:contain"/>`
    : `<div style="height:48px;width:48px;border-radius:8px;background:rgba(255,255,255,0.2);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:18px">${(hospital.name || 'HMS').charAt(0)}</div>`;

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Receipt ${receipt.receipt_number || ''}</title>
<style>
  *{box-sizing:border-box;font-family:'Inter','Segoe UI',system-ui,sans-serif;-webkit-print-color-adjust:exact;color-adjust:exact}
  body{margin:0;padding:24px;color:#0f172a;background:#f1f5f9}
  .doc{max-width:780px;margin:0 auto;background:#fff;border-radius:14px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.06)}
  .head{background:linear-gradient(135deg, ${primaryColor}, ${accentColor});color:#fff;padding:28px 32px;display:flex;justify-content:space-between;align-items:flex-start;gap:24px}
  .head h1{margin:0;font-size:22px;letter-spacing:.3px;font-weight:700}
  .head .sub{font-size:12px;opacity:.9;margin-top:4px}
  .head .hospital{font-weight:700;font-size:16px}
  .head .meta{font-size:11px;opacity:.85;margin-top:2px}
  .head-right{text-align:right}
  .rcpt-no{font-size:20px;font-weight:800;letter-spacing:.5px}
  .body{padding:24px 32px}
  .section{margin-bottom:22px}
  .section h3{font-size:11px;text-transform:uppercase;letter-spacing:.8px;color:#94a3b8;font-weight:700;margin:0 0 8px}
  .grid2{display:grid;grid-template-columns:1fr 1fr;gap:18px}
  .grid3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:18px}
  .row{display:flex;justify-content:space-between;font-size:13px;padding:4px 0}
  .row .k{color:#64748b}
  .row .v{color:#0f172a;font-weight:500;text-align:right}
  table{width:100%;border-collapse:collapse;font-size:13px}
  table th{background:#f8fafc;text-align:left;padding:8px 10px;font-size:11px;text-transform:uppercase;color:#64748b;letter-spacing:.4px}
  table tbody td{border-top:1px solid #e2e8f0;padding:8px 10px}
  .totals{margin-top:14px;border-top:2px dashed #e2e8f0;padding-top:14px;display:flex;justify-content:flex-end}
  .totals-inner{min-width:260px}
  .totals .row{padding:6px 0}
  .grand{font-size:18px;color:${primaryColor};font-weight:800}
  .footer{padding:16px 32px;background:#f8fafc;color:#64748b;font-size:11px;text-align:center;border-top:1px solid #e2e8f0}
  .stamp{display:inline-block;border:1.5px dashed ${accentColor};color:${accentColor};padding:6px 12px;border-radius:6px;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.6px;margin-top:8px}
  .badge-vip{display:inline-block;background:#fef3c7;color:#a16207;padding:2px 8px;border-radius:99px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.4px;margin-left:6px}
  @media print { body{padding:0;background:#fff} .doc{box-shadow:none;border-radius:0} }
</style></head><body>
<div class="doc">
  <div class="head">
    <div style="display:flex;gap:14px;align-items:flex-start">
      ${logoHtml}
      <div>
        <div class="hospital">${hospital.name || 'Hospital'}</div>
        <div class="meta">${[hospital.address, hospital.city, hospital.state, hospital.pincode].filter(Boolean).join(', ')}</div>
        <div class="meta">${[hospital.phone && `Phone: ${hospital.phone}`, hospital.email].filter(Boolean).join(' · ')}</div>
      </div>
    </div>
    <div class="head-right">
      <h1>Payment Receipt</h1>
      <div class="sub">Tax Invoice · cum · Receipt</div>
      <div class="rcpt-no" style="margin-top:10px">${receipt.receipt_number || '—'}</div>
      <div class="sub">Issued: ${issued}</div>
    </div>
  </div>

  <div class="body">
    <div class="section grid2">
      <div>
        <h3>Patient</h3>
        <div class="row"><span class="k">Name</span><span class="v">${fullName}${p.is_vip ? '<span class="badge-vip">VIP</span>' : ''}</span></div>
        <div class="row"><span class="k">UHID</span><span class="v">${p.patient_id || '—'}</span></div>
        <div class="row"><span class="k">Gender / Age</span><span class="v">${(p.gender || '—')}${calcAge(p.date_of_birth) ? ' · ' + calcAge(p.date_of_birth) : ''}</span></div>
        <div class="row"><span class="k">Phone</span><span class="v">${p.phone || '—'}</span></div>
        <div class="row"><span class="k">Address</span><span class="v" style="max-width:60%">${[p.address, p.city, p.state, p.pincode].filter(Boolean).join(', ') || '—'}</span></div>
      </div>
      <div>
        <h3>Collected By</h3>
        <div class="row"><span class="k">Staff</span><span class="v">${receipt.collected_by_name || '—'}</span></div>
        <div class="row"><span class="k">Date</span><span class="v">${issued}</span></div>
        ${receipt.invoice_id ? `<div class="row"><span class="k">Invoice Ref</span><span class="v">${receipt.invoice_id}</span></div>` : ''}
        ${receipt.service_name ? `<div class="row"><span class="k">Category</span><span class="v">${receipt.service_name}</span></div>` : ''}
        ${hospital.gstin ? `<div class="row"><span class="k">Hospital GSTIN</span><span class="v">${hospital.gstin}</span></div>` : ''}
      </div>
    </div>

    <div class="section">
      <h3>Service</h3>
      <table>
        <thead><tr><th>Description</th><th style="text-align:right">Amount</th></tr></thead>
        <tbody>
          <tr><td>${receipt.description || receipt.service_name || 'Hospital service charge'}</td><td style="text-align:right;font-variant-numeric:tabular-nums">${formatMoney(baseAmount)}</td></tr>
        </tbody>
      </table>
    </div>

    <div class="section">
      <h3>Payment Breakdown</h3>
      <table>
        <thead><tr><th>Method</th><th>Reference</th><th style="text-align:right">Amount</th></tr></thead>
        <tbody>${splitsRows}</tbody>
      </table>
    </div>

    <div class="totals">
      <div class="totals-inner">
        <div class="row"><span class="k">Subtotal</span><span class="v" style="font-variant-numeric:tabular-nums">${formatMoney(baseAmount)}</span></div>
        ${taxAmount > 0 ? `<div class="row"><span class="k">Tax / GST</span><span class="v" style="font-variant-numeric:tabular-nums">${formatMoney(taxAmount)}</span></div>` : ''}
        <div class="row grand"><span>Total Paid</span><span style="font-variant-numeric:tabular-nums">${formatMoney(grand)}</span></div>
        <div class="stamp">Paid in Full</div>
      </div>
    </div>
  </div>

  <div class="footer">
    Thank you for choosing ${hospital.name || 'us'}. This is a computer-generated receipt and does not require a physical signature.
    ${hospital.gstin ? `<br/>GSTIN: ${hospital.gstin}` : ''}
  </div>
</div>
<script>window.onload=()=>setTimeout(()=>window.print(),300);</script>
</body></html>`;
}

export function printReceiptHTML(args) {
  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) {
    // eslint-disable-next-line no-alert
    alert('Pop-up blocked. Please allow pop-ups to print the invoice.');
    return false;
  }
  w.document.write(buildReceiptHTML(args));
  w.document.close();
  return true;
}
